import classNames from "classnames";
import { Team, User } from "~/normalizr/entities";
import { messageBoardResx } from "~/resxDist/messageBoardResx";
import { linkifyTextString } from "~/utils/linkifyTextString";
import { buildMentionName, characterPattern, findMentions, notCharacterPattern } from "./utils";

type MentionsHash = {
    [key: string]: Partial<User & Team>;
};

const emailOnlyTitle = ` title="${messageBoardResx.email_only_mention_title}"`,
    emailOnlyPrefix = `<span class="email_only_prefix"${emailOnlyTitle}>Email</span>`,
    emptyString = "";

export class MessageFormatter {
    _mentionsHash: MentionsHash;
    _mentionsRegex: RegExp;

    constructor(users: User[], teams: Team[]) {
        this._mentionsHash = {};

        for (const team of teams) {
            this._mentionsHash[buildMentionName(team.Name).toLowerCase()] = team;
        }

        for (const user of users) {
            this._mentionsHash[buildMentionName(user.Name).toLowerCase()] = user;
        }

        this._mentionsRegex = this._buildMentionsRegexp();
    }

    _buildMentionsRegexp() {
        return new RegExp(`((?:${notCharacterPattern})|^)@(${characterPattern}+)`, "ig");
    }

    format(text: string, emailOnlyMentions?: Dictionary<boolean>) {
        const result = linkifyTextString(text, "_blank");
        return this._formatMentions(result, emailOnlyMentions);
    }

    findMentionSelections(text: string) {
        const regexp = this._buildMentionsRegexp(); // create new instance of regexp because exec is stateful
        return findMentions(regexp, text, this._mentionsHash, (mention, item) => ({
            id: item.UserId,
            teamId: item.TeamId,
            users: item.Users,
            name: mention,
            title: item.Name,
        }));
    }

    _formatMentions(text: string, emailOnlyMentions?: Dictionary<boolean>) {
        return text.replace(this._mentionsRegex, (match, before, mention) => {
            const mentionedUser = this._mentionsHash[mention.toLowerCase()];

            if (mentionedUser) {
                const currentUserId = ep.settings.currentUserId;
                const teamUsers = this._mentionsHash[mention.toLowerCase()]?.Users;
                const highlightClass = classNames(
                    "highlight_mention",
                    emailOnlyMentions?.[mentionedUser.UserId] && "email_only",
                    (mentionedUser.UserId === currentUserId || teamUsers?.includes(currentUserId)) && "yourself"
                );

                const title = emailOnlyMentions?.[mentionedUser.UserId] ? emailOnlyTitle : emptyString;
                const prefix = emailOnlyMentions?.[mentionedUser.UserId] ? emailOnlyPrefix : emptyString;

                return `${before}${prefix}<span class="${highlightClass} fs-block"${title}>@${mention}</span>`;
            }

            return match;
        });
    }
}

export const emptyMessageFormatter = new MessageFormatter([], []);
